import { Component, Element, Event, EventEmitter, Host, Listen, Method, Prop, Watch, h } from '@stencil/core';
import { VirtualPageTrackingEvent } from '@union-investment/tracking/src/events';
import { isMagnoliaEditingMode } from '../../helper';

/**
 * Structure of data passed from Magnolia through the 'body' prop
 */
interface UiCmsSectionTrackingBody {
  name: string;
}

/**
 * UiCmsSectionTracking component tracks sections based on visibility.
 * It is designed to be used at the top of a section and will fire an event when the section becomes visible.
 * Configure the rootMargin below to determine when precisely the tracking event is fired.
 */
@Component({
  tag: 'ui-cms-section-tracking',
  styleUrls: { default: 'ui-cms-section-tracking.css', editing: 'ui-cms-section-tracking.magnolia.scss' },
  shadow: true,
})
export class UiCmsSectionTracking {
  @Element() el: HTMLUiCmsSectionTrackingElement;

  @Prop({ mutable: true }) body: UiCmsSectionTrackingBody = {} as any;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Watch('utilityBody')
  bodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  /**
   * Margin around the root for IntersectionObserver calculations.
   * Can have values similar to CSS margin property,
   * e.g. "10px 20px 30px 40px" (top, right, bottom, left).
   * The values can be percentages.
   * This set of values serves to grow or shrink each side of the root element's bounding box
   * before computing intersections.
   * Defaults to all zeros.
   * Below we use a negative bottom margin by default to trigger the event after the element is 100px into the viewport.
   */
  @Prop() rootMargin = '0px 0px -100px 0px';

  /**
   * Initial delay in ms before invocation of this.intersectionObserver.observe().
   * This ensures section tracking events are only fired after the initial render / load phase.
   */
  @Prop() initDelay = 1500;

  /**
   * Gate to prevent section tracking events on scroll by (e.g. clicking on anchor link to a further section).
   */
  private preventSectionTrackingEvent = false;

  /**
   * Event emitted when the section becomes visible (tracked)
   */
  @Event() sectionTrack: EventEmitter<HTMLUiCmsSectionTrackingElement>;

  /**
   * When another ui-cms-section-tracking element fires a sectionTrack event after an anchorCall, we need to re-enable tracking.
   */
  @Listen('sectionTrack', { target: 'window' })
  handleOtherSectionTrack(event: CustomEvent<HTMLUiCmsSectionTrackingElement>) {
    // if the event comes from another section and this seciton is disabled, re-enable it
    if (this.el !== event.detail && this.preventSectionTrackingEvent) {
      this.preventSectionTrackingEvent = false;
    }
  }

  /**
   * Tracks the section (emits event and calls tracking service)
   */
  @Method()
  async track() {
    this.sectionTrack.emit(this.el);

    window.ui?.services?.tracking?.track({
      eventInfo: {
        eventName: `Section Tracking ${this.body.name}`,
        eventType: 'VirtualPage',
        eventCaller: this.el,
      },
      attributes: {
        virtualContentGroups: [this.body.name],
        isInitial: this.sectionIndex === 0,
      },
    } as VirtualPageTrackingEvent);
  }

  /**
   * Index of ui-cms-section-tracking element instance.
   * We usually have multiple instances within the document.
   */
  private sectionIndex: number;

  /**
   * IntersectionObserver instance for tracking the section visibility
   */
  private intersectionObserver: IntersectionObserver;

  /**
   * Initializes the IntersectionObserver with options
   */
  private initIntersectionObserver() {
    this.intersectionObserver?.disconnect();

    const callback = (entries) => {
      if (!this.preventSectionTrackingEvent && entries.some((entry) => entry.isIntersecting)) {
        this.track();
        // disconnect the observer after the section has been tracked
        this.intersectionObserver.disconnect();
      }
    };

    const options = {
      rootMargin: this.rootMargin,
      threshold: 0.01,
    };

    this.intersectionObserver = new IntersectionObserver(callback, options);
  }

  /**
   * Listens for the `uiCmsLinkAnchorCall` custom event dispatched from ui-cms-link.
   *
   * @param event {CustomEvent<{ name: string }>} The custom event object containing details about the clicked anchor link.
   */
  @Listen('anchorCall', { target: 'window' })
  handleAnchorCall(event: CustomEvent<{ name: string }>) {
    if (event.detail.name?.toLowerCase() !== this.body.name?.toLowerCase()) {
      // block section tracking events for a short period of time to avoid tracking skipped sections
      this.preventSectionTrackingEvent = true;
    }
  }

  componentWillLoad() {
    // parse the utility body string to an object
    this.bodyChangeHandler(this.utilityBody);

    // get all ui-cms-section-tracking instances in the document and save the index of the current instance
    const elements = [...document.getElementsByTagName(this.el.tagName)];
    this.sectionIndex = elements.indexOf(this.el);

    // initialize the IntersectionObserver
    this.initIntersectionObserver();
  }

  // While content might be loaded, layout adjustments and browser optimizations can still occur, triggering reflows and repaints, therefore we use a delay.
  componentDidLoad() {
    setTimeout(() => {
      this.intersectionObserver.observe(this.el);
    }, this.initDelay);
  }

  disconnectedCallback() {
    // ensure intersectionObserver is disconnected with component
    this.intersectionObserver?.disconnect();
  }

  render() {
    return <Host>{isMagnoliaEditingMode() && `Section Tracking: ${this.body.name}`}</Host>;
  }
}
